import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import THEME from '../../constants/theme';

/**
 * ContactModal component - displays email contact information with copy-to-clipboard
 * Replaces Calendly scheduling links
 *
 * @param {boolean} open - Whether modal is open
 * @param {function} onClose - Function to call when modal closes
 * @returns {React.Component} ContactModal component
 */
const ContactModal = ({ open, onClose }) => {
  const [copied, setCopied] = useState(false);
  const email = 'investor-info@mqcapital.co.uk';

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Silently ignore copy failures to satisfy lint rules
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
          borderRadius: '12px',
          border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}30`,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
          color: THEME.COLORS.NEUTRAL_BLACK
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontWeight: 600,
            color: THEME.COLORS.NEUTRAL_BLACK
          }}
        >
          Schedule a Meeting
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: THEME.COLORS.NEUTRAL_BLACK,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            }
          }}
          aria-label='Close modal'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 4 }}>
        <Typography
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            color: THEME.COLORS.NEUTRAL_BLACK,
            mb: 3,
            lineHeight: 1.6
          }}
        >
          To schedule a meeting with our team, please send an email to:
        </Typography>

        <Box
          sx={{
            backgroundColor: `${THEME.COLORS.PRIMARY_BLUE}15`,
            border: `2px solid ${THEME.COLORS.PRIMARY_BLUE}`,
            borderRadius: '12px',
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            alignItems: 'center'
          }}
        >
          <Typography
            sx={{
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '1.1rem',
              fontWeight: 500,
              color: THEME.COLORS.PRIMARY_BLUE,
              wordBreak: 'break-all',
              textAlign: 'center'
            }}
          >
            {email}
          </Typography>

          <Button
            variant='contained'
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopyEmail}
            sx={{
              backgroundColor: copied
                ? THEME.COLORS.SUCCESS || '#4caf50'
                : THEME.COLORS.PRIMARY_BLUE,
              color: THEME.COLORS.NEUTRAL_WHITE,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              textTransform: 'none',
              fontSize: '0.95rem',
              padding: '0.6rem 1.5rem',
              borderRadius: '6px',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: copied
                  ? THEME.COLORS.SUCCESS || '#45a049'
                  : THEME.COLORS.PRIMARY_BLUE_HOVER || '#0028cc',
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${THEME.COLORS.PRIMARY_BLUE}40`
              }
            }}
          >
            {copied ? 'Copied!' : 'Copy Email'}
          </Button>
        </Box>

        <Typography
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '0.9rem',
            color: THEME.COLORS.NEUTRAL_BLACK,
            mt: 3,
            textAlign: 'center',
            lineHeight: 1.5
          }}
        >
          Our team will respond to your inquiry when available.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
